import { tr } from '@/lib/i18n-core';
import { useState, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Wallet } from 'lucide-react';
import { get } from '@/lib/api';
import { describeError, useAuth } from '@/lib/auth';
import { useToast } from '@/components/ui/toast';
import { payBillPath } from '../pay-bill';
import { humanize, timeOnly } from '@/lib/utils';
import { Dialog } from '@/components/ui/layout';
import { Badge, Button, PopoverContent, StatusBadge } from '@/components/ui/primitives';
import { CancelAppointmentDialog } from '../CancelAppointmentDialog';
import { PayerDialog, RescheduleDialog, printSlip } from '../FrontDeskDialogs';
import type { CalendarAppointment, CalendarProvider } from './calendar-types';
import { durationLabel } from './calendar-time';
import { useAppointmentActions } from './useAppointmentActions';

export interface AppointmentCardProps {
  appointment: CalendarAppointment;
  provider: Pick<CalendarProvider, 'id' | 'name'>;
  onClose: () => void;
  onChanged: () => void;
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-3 text-sm">
      <span className="shrink-0 text-xs text-muted-foreground">{label}</span>
      <span className="truncate text-end">{children}</span>
    </div>
  );
}

export function AppointmentCardBody({ appointment, provider, onClose, onChanged }: AppointmentCardProps) {
  const { can } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const [findingBill, setFindingBill] = useState(false);
  const { arrive, changeStatus } = useAppointmentActions();
  const [moving, setMoving] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [changingPayer, setChangingPayer] = useState(false);

  const deskShape = {
    id: appointment.id,
    slotAt: appointment.startsAt,
    doctor: { id: provider.id, name: provider.name },
    patient: { id: appointment.patientId, fullName: appointment.patientName },
    payer: appointment.payerName ? { id: 'current', name: appointment.payerName } : null,
  };
  const manage = can('appointment.manage');
  const desk = can('appointment.manage', 'token.issue');
  const open = ['booked', 'confirmed'].includes(appointment.status);
  const done = () => {
    onChanged();
    onClose();
  };
  const billable = can('payment.collect') && ['arrived', 'in_consultation', 'completed'].includes(appointment.status);
  const payBill = async () => {
    setFindingBill(true);
    try {
      const { invoiceId } = await get<{ invoiceId: string | null }>(`/appointments/${appointment.id}/payable-invoice`);
      if (invoiceId) {
        onClose();
        navigate(payBillPath(invoiceId, '/appointments'));
      } else {
        toast.success(tr('opd', 'nothing_left_to_pay_on_this_visit'));
      }
    } catch (error) {
      toast.error(tr('opd', 'could_not_open_the_bill'), describeError(error));
    } finally {
      setFindingBill(false);
    }
  };

  return (
    <div className="w-72 max-w-full space-y-3">
      <div>
        <Link to={`/patients/${appointment.patientId}`} className="font-semibold hover:underline">
          {appointment.patientName}
        </Link>
        <p className="text-xs text-muted-foreground">
          {appointment.mrn} · {timeOnly(appointment.startsAt)}–{timeOnly(appointment.endsAt)}
          {appointment.durationMinutes > 0 ? ` · ${durationLabel(appointment.durationMinutes)}` : ''}
        </p>
      </div>
      <div className="flex flex-wrap gap-1">
        <StatusBadge status={appointment.status} />
        {appointment.tokenNo && <Badge variant="outline">#{appointment.tokenNo}</Badge>}
        {appointment.isWalkIn && <Badge variant="muted">{tr('opd', 'walk_in')}</Badge>}
        {appointment.priority === 'urgent' && <Badge variant="destructive">{tr('opd', 'see_first')}</Badge>}
        {appointment.feeAlert === 'refund_due' && <Badge variant="destructive">{tr('opd', 'refund_due')}</Badge>}
        {appointment.feeAlert === 'fee_review' && <Badge variant="warning">{tr('opd', 'doctor_changed_check_fee')}</Badge>}
      </div>
      <div className="space-y-1">
        <Row label={tr('opd', 'doctor')}>{provider.name}</Row>
        <Row label={tr('opd', 'service')}>{appointment.serviceName ?? tr('opd', 'consultation')}</Row>
        <Row label={tr('opd', 'payer')}>{appointment.payerName ?? tr('opd', 'self_pay')}</Row>
        <Row label={tr('opd', 'visit')}>{humanize(appointment.type)}</Row>
        {appointment.reason && <Row label={tr('opd', 'reason')}>{appointment.reason}</Row>}
      </div>
      {billable && (
        <Button size="sm" variant="success" className="w-full" onClick={() => void payBill()} loading={findingBill}>
          <Wallet className="h-3.5 w-3.5" aria-hidden /> {tr('opd', 'pay_bill')}
        </Button>
      )}
      {desk && (
        <div className="flex flex-wrap gap-1 border-t pt-3">
          {open && (
            <>
              <Button size="sm" onClick={() => arrive.mutate(appointment.id, { onSuccess: onClose })} loading={arrive.isPending}>
                {tr('opd', 'arrived')}
              </Button>
              {appointment.status === 'booked' && (
                <Button size="sm" variant="ghost" onClick={() => changeStatus.mutate({ id: appointment.id, next: 'confirmed' }, { onSuccess: onClose })}>
                  {tr('opd', 'confirm')}
                </Button>
              )}
              <Button size="sm" variant="ghost" onClick={() => changeStatus.mutate({ id: appointment.id, next: 'no_show' }, { onSuccess: onClose })}>
                {tr('opd', 'no_show')}
              </Button>
            </>
          )}
          {appointment.status === 'arrived' && (
            <Button size="sm" variant="outline" onClick={() => void printSlip(appointment.id)}>
              {tr('opd', 'slip')}
            </Button>
          )}
          {['booked', 'confirmed', 'no_show'].includes(appointment.status) && manage && (
            <Button size="sm" variant="ghost" onClick={() => setMoving(true)}>
              {tr('opd', 'move')}
            </Button>
          )}
          {!['completed', 'cancelled', 'no_show'].includes(appointment.status) && manage && (
            <Button size="sm" variant="ghost" onClick={() => setChangingPayer(true)}>
              {tr('opd', 'payer')}
            </Button>
          )}
          {['booked', 'confirmed', 'arrived'].includes(appointment.status) && (
            <Button size="sm" variant="ghost" className="text-destructive" onClick={() => setCancelling(true)}>
              {tr('opd', 'cancel')}
            </Button>
          )}
        </div>
      )}

      {moving && <RescheduleDialog appointment={deskShape} onClose={() => setMoving(false)} onDone={done} />}
      {changingPayer && <PayerDialog appointment={deskShape} onClose={() => setChangingPayer(false)} onDone={done} />}
      {cancelling && <CancelAppointmentDialog appointment={deskShape} onClose={() => setCancelling(false)} onDone={done} />}
    </div>
  );
}

export function AppointmentHoverCard(
  props: AppointmentCardProps & { onPointerEnter?: () => void; onPointerLeave?: () => void; onPin?: () => void },
) {
  const { onPointerEnter, onPointerLeave, onPin, ...card } = props;
  return (
    <PopoverContent
      side="right"
      align="start"
      className="w-auto"
      onPointerEnter={onPointerEnter}
      onPointerLeave={onPointerLeave}
      onClickCapture={onPin}
      onOpenAutoFocus={(event) => event.preventDefault()}
    >
      <AppointmentCardBody {...card} />
    </PopoverContent>
  );
}

export function AppointmentSheet(props: AppointmentCardProps) {
  return (
    <Dialog open onClose={props.onClose} title={props.appointment.patientName} description={`${props.appointment.mrn} · ${timeOnly(props.appointment.startsAt)}`}>
      <AppointmentCardBody {...props} />
    </Dialog>
  );
}
